import { useRef, useEffect, memo } from "react";
import { Marker, Tooltip, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";

// Signatur til at sammenligne en point-prop på tværs af renders uden at lave dybtgående object comparison.
// Returnerer en kort string der unikt identificerer alt det visuelle output afhænger af.
function pointSignature(p) {
  if (!p) return '';
  const containers = (p.containers || []).map(c => `${c.fraction_type || ''}|${c.container_size || ''}|${c.count || 1}`).join(',');
  const bins = (p.bins || []).map(b => `${b.fraction_type || ''}|${b.container_size || ''}|${b.count || 1}`).join(',');
  const markers = (p.marker_ids || []).join(',');
  return `${p.id}|${p.status}|${p.setup_status}|${p.qr_ref}|${p.bin_fraction || ''}|${containers}|${bins}|${markers}`;
}

function markerPropsEqual(prev, next) {
  // Position (array) — sammenlign værdier i stedet for reference
  if (prev.position?.[0] !== next.position?.[0] || prev.position?.[1] !== next.position?.[1]) return false;
  // Simple felter
  if (prev.id !== next.id) return false;
  if (prev.name !== next.name) return false;
  if (prev.canDrag !== next.canDrag) return false;
  if (prev.statusPriority !== next.statusPriority) return false;
  // Icon: ikon-cache i mapIcons.js returnerer samme ref for samme signatur, så ref-sammenligning er OK
  if (prev.icon !== next.icon) return false;
  // Point: brug signatur (tjekker status, containers, bins, marker_ids)
  if (pointSignature(prev.point) !== pointSignature(next.point)) return false;
  // attentionMarkers: kun længde + id-signatur — komplet sjælden ændring
  const pAm = prev.attentionMarkers || [];
  const nAm = next.attentionMarkers || [];
  if (pAm.length !== nAm.length) return false;
  // sizeLiters / fractionOrder: stabile refs hvis brugt korrekt — preserve som er
  if (prev.sizeLiters !== next.sizeLiters) return false;
  if (prev.fractionOrder !== next.fractionOrder) return false;
  // onClick/onDragEnd: callbacks får vi at vide via useCallback at de er stabile.
  // Vi vælger bevidst IKKE at sammenligne dem — minor risiko at en ændret callback ikke bliver hentet.
  return true;
}

export const DraggableMarker = memo(function DraggableMarker({ position, id, onDragEnd, onClick, icon, name, canDrag = true, statusPriority = 0, point, sizeLiters, fractionOrder, attentionMarkers = [] }) {
  const markerRef = useRef(null);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.options.statusPriority = statusPriority;
    }
  }, [statusPriority]);

  const eventHandlers = {
    dragend() {
      if (!canDrag) return;
      const marker = markerRef.current;
      if (marker != null) {
        const pos = marker.getLatLng();
        onDragEnd(id, pos.lat, pos.lng);
      }
    },
    click(e) {
      L.DomEvent.stopPropagation(e);
      onClick?.(id);
    },
  };

  // Specielle tags
  const tags = [];
  // Opmærksomhedsmarkører tilknyttet via marker_ids — vis først
  if (point?.marker_ids && attentionMarkers.length > 0) {
    point.marker_ids.forEach(mid => {
      const m = attentionMarkers.find(x => x.id === mid);
      if (m && !tags.includes(m.name)) tags.push(m.name);
    });
  }
  const fo = fractionOrder || [];
  const allItems = [...(point?.containers || []), ...(point?.bins || [])]
    .sort((a, b) => {
      const ai = fo.indexOf(a.fraction_type);
      const bi = fo.indexOf(b.fraction_type);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  allItems.forEach(c => {
    const ft = (c.fraction_type || '').toLowerCase();
    if (ft === 'tømmes udenfor afvikling' && !tags.includes('Tømmes udenfor afvikling')) tags.push('Tømmes udenfor afvikling');
    if (ft === 'dropzone' && !tags.includes('Dropzone')) tags.push('Dropzone');
    const cs = (c.container_size || '').toLowerCase();
    if (cs === 'træstativ' && !tags.includes('Træstativ')) tags.push('Træstativ');
  });

  // Beregn total kapacitet (kun frontend, ingen API)
  let totalCap = 0;
  const sl = sizeLiters || {};
  allItems.forEach(c => { totalCap += (c.count || 1) * (sl[c.container_size] || 0); });

  return (
    <Marker
      draggable={canDrag}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={icon}
    >
      {name && (
        <Tooltip direction="top" offset={[0, -24]} className="point-tooltip">
          <div style={{ fontSize: '11px', fontFamily: 'monospace', minWidth: '140px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: tags.length ? '0' : '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span>{name}</span>
              {point?.qr_ref && <span style={{ fontSize: '10px', fontWeight: 'bold', background: 'rgba(200,220,80,0.25)', border: '1px solid rgba(200,220,80,0.6)', borderRadius: '3px', padding: '1px 5px', letterSpacing: '0.5px' }}>{point.qr_ref}</span>}
              {tags.length > 0 ? <span style={{ fontSize: '10px', fontWeight: 'normal', opacity: 0.7 }}>· {tags.join(' · ')}</span> : null}
            </div>
            {allItems.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                <span>{c.count || 1}x {c.fraction_type}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{c.container_size || ''}</span>
              </div>
            ))}
            {totalCap > 0 && (
              <div style={{ borderTop: '1px solid #ccc', marginTop: '3px', paddingTop: '3px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                <span>Total</span><span>{totalCap.toLocaleString('da-DK')}L</span>
              </div>
            )}
          </div>
        </Tooltip>
      )}
    </Marker>
  );
}, markerPropsEqual);

function maxiPropsEqual(prev, next) {
  if (prev.statusLabel !== next.statusLabel) return false;
  if (prev.icon !== next.icon) return false;
  const a = prev.container || {};
  const b = next.container || {};
  return (
    a.id === b.id &&
    a.lat === b.lat &&
    a.lng === b.lng &&
    a.name === b.name &&
    a.qr_ref === b.qr_ref &&
    a.status === b.status &&
    a.container_type === b.container_type &&
    a.fraction === b.fraction
  );
}

export const DraggableMaxiMarker = memo(function DraggableMaxiMarker({ container, icon, statusLabel, onDragEnd, onClick }) {
  const markerRef = useRef(null);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const pos = marker.getLatLng();
        onDragEnd(container.id, pos.lat, pos.lng);
      }
    },
    click(e) {
      L.DomEvent.stopPropagation(e);
      onClick?.(container);
    },
  };

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={[container.lat, container.lng]}
      ref={markerRef}
      icon={icon}
    >
      <Tooltip direction="top" offset={[0, -20]} className="maxi-tooltip">
        <div style={{ minWidth: '140px', padding: '4px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span>{container.name}</span>
            {container.qr_ref && <span style={{ fontSize: '10px', fontWeight: 'bold', background: 'rgba(200,220,80,0.25)', border: '1px solid rgba(200,220,80,0.6)', borderRadius: '3px', padding: '1px 5px', letterSpacing: '0.5px', fontFamily: 'monospace' }}>{container.qr_ref}</span>}
          </div>
          <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>
            <strong>Type:</strong> {container.container_type || 'Ikke angivet'}
          </div>
          <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>
            <strong>Fraktion:</strong> {container.fraction || 'Ikke angivet'}
          </div>
          <div style={{ 
            fontSize: '11px', 
            fontWeight: 'bold',
            color: statusLabel === 'Tømt' ? '#22c55e' : 
                   statusLabel === '75% fuld' ? '#f97316' : 
                   statusLabel === 'Skal tømmes' ? '#ef4444' : '#eab308'
          }}>
            Status: {statusLabel}
          </div>
        </div>
      </Tooltip>
    </Marker>
  );
}, maxiPropsEqual);

export function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export function ZoomTracker({ onZoomChange }) {
  const map = useMap();
  useEffect(() => {
    const handleZoom = () => onZoomChange(map.getZoom());
    handleZoom();
    map.on('zoomend', handleZoom);
    return () => map.off('zoomend', handleZoom);
  }, [map, onZoomChange]);
  return null;
}

export function MapPositionSaver({ eventId }) {
  const map = useMap();
  
  useEffect(() => {
    if (!eventId) return;
    
    const savePosition = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      localStorage.setItem(`mapPosition_${eventId}`, JSON.stringify({
        lat: center.lat, lng: center.lng, zoom, timestamp: Date.now()
      }));
    };
    
    let timeoutId;
    const debouncedSave = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(savePosition, 500);
    };
    
    map.on('moveend', debouncedSave);
    map.on('zoomend', debouncedSave);
    
    return () => {
      clearTimeout(timeoutId);
      map.off('moveend', debouncedSave);
      map.off('zoomend', debouncedSave);
    };
  }, [map, eventId]);
  
  return null;
}

export function MapController({ center, zoom, hasDefaultLocation, eventId, overlayBounds }) {
  const map = useMap();
  const initializedRef = useRef(false);
  
  useEffect(() => {
    if (initializedRef.current) return;
    
    if (eventId) {
      const saved = localStorage.getItem(`mapPosition_${eventId}`);
      if (saved) {
        try {
          const pos = JSON.parse(saved);
          if (Date.now() - pos.timestamp < 24 * 60 * 60 * 1000) {
            map.setView([pos.lat, pos.lng], pos.zoom, { animate: false });
            initializedRef.current = true;
            return;
          }
        } catch (e) {
          console.error('Kunne ikke gendanne kort-position:', e);
        }
      }
    }
    
    if (center && hasDefaultLocation) {
      map.setView(center, zoom || 15, { animate: false });
      initializedRef.current = true;
    } else if (overlayBounds && overlayBounds.length === 2) {
      // Centrér på overlay hvis ingen gemt position
      const lat = (overlayBounds[0][0] + overlayBounds[1][0]) / 2;
      const lng = (overlayBounds[0][1] + overlayBounds[1][1]) / 2;
      map.setView([lat, lng], 16, { animate: false });
      initializedRef.current = true;
    }
  }, [center, zoom, map, hasDefaultLocation, eventId, overlayBounds]);
  
  return null;
}
